import { each } from 'lodash';
import { ToastProgrammatic } from 'buefy';
import { BaseUIService } from '/@/modules/core/ui/services/BaseUIService';
import ComponentService from '@/modules/core/app/services/ComponentService';
import ToastView from '@/modules/core/ui/components/presentational/ToastView.vue';
import { ToastType } from '@/modules/core/ui/enums/ToastType';
import type { App } from 'vue';

let toastInstance: ToastProgrammatic | null = null;

type GlobalPositions =
  | 'is-top-right'
  | 'is-top'
  | 'is-top-left'
  | 'is-bottom-right'
  | 'is-bottom'
  | 'is-bottom-left';

interface BNoticeComponent {
  close: () => void;
  $el: HTMLElement;
}

type ToastOptions = {
  type?: string;
  position?: GlobalPositions;
  duration?: number;
  container?: string;
  queue?: boolean;
  hasIcon?: boolean;
  hasCloseButton?: boolean;
  actionText?: string;
  onAction?: () => void;
};

export class ToastService extends BaseUIService {
  setApp(app: App): void {
    toastInstance = new ToastProgrammatic(app);
  }

  show(messages: string | string[], options: ToastOptions = {}): void {
    if (!toastInstance) {
      throw new Error('ToastService: app not initialized. Call setApp() first.');
    }

    const instance: ToastProgrammatic = toastInstance;
    const state: ToastType = this.resolveType(options.type);
    const duration: number = options.duration ?? 4000;
    const messagesToShow: string[] = messages instanceof Array ? messages : [messages];

    each(messagesToShow, (message: string): void => {
      const notice: BNoticeComponent = instance.open({
        message: ComponentService.renderComponentToHtml(ToastView, {
          type: state,
          message,
          hasIcon: options.hasIcon ?? true,
          hasCloseButton: options.hasCloseButton ?? true,
          actionText: options.actionText
        }),
        type: state.key,
        duration,
        position: options.position,
        container: options.container,
        queue: options.queue
      }) as unknown as BNoticeComponent;

      this.bindNoticeActions(notice, options);
    });
  }

  success(messages: string | string[], options: ToastOptions = {}): void {
    this.show(messages, { ...options, type: ToastType.SUCCESS.key });
  }

  error(messages: string | string[], options: ToastOptions = {}): void {
    this.show(messages, { ...options, type: ToastType.ERROR.key });
  }

  warn(messages: string | string[], options: ToastOptions = {}): void {
    this.show(messages, { ...options, type: ToastType.WARNING.key });
  }

  info(messages: string | string[], options: ToastOptions = {}): void {
    this.show(messages, { ...options, type: ToastType.DEFAULT.key });
  }

  private bindNoticeActions(notice: BNoticeComponent, options: ToastOptions): void {
    notice.$el
      ?.querySelector('.ce-toast-close')
      ?.addEventListener('click', (): void => notice.close());

    if (!options.onAction) {
      return;
    }

    notice.$el?.querySelector('.ce-toast-action')?.addEventListener('click', (): void => {
      options.onAction?.();
      notice.close();
    });
  }

  private resolveType(type?: string): ToastType {
    switch (type) {
      case ToastType.SUCCESS.key:
        return ToastType.SUCCESS;
      case ToastType.ERROR.key:
      case 'error':
        return ToastType.ERROR;
      case ToastType.WARNING.key:
        return ToastType.WARNING;
      default:
        return ToastType.DEFAULT;
    }
  }
}

export default new ToastService();
